import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import {
  AlertController,
  LoadingController,
  ToastController,
} from "@ionic/angular";
import { DataSourceConfig } from "../utils/datasource";

export function getFilterActionCount(
  datasourceConfig: DataSourceConfig,
  filterForm: FormGroup
) {
  let queryParams = { ...datasourceConfig.queryParams } as any;
  delete queryParams.search; // Remove search from filterCount
  let filterCount = 0;
  for (let formControlKey in queryParams) {
    if (filterForm.controls[formControlKey]) {
      filterCount++;
    }
  }
  return filterCount;
}

export function updateFilterAction(
  params: { [key: string]: string },
  datasourceConfig: DataSourceConfig
): DataSourceConfig {
  let queryParams = { ...datasourceConfig.queryParams, ...params };
  for (let k in queryParams) {
    let value = queryParams[k];
    if (value == undefined || value == "all") delete queryParams[k];
    if (k == "search" && value == "") delete queryParams[k]; // Remove empty search
  }
  datasourceConfig = {
    ...datasourceConfig,
    queryParams: { ...queryParams },
  };
  return datasourceConfig;
}

@Injectable({ providedIn: "root" })
export class DeleteActionService {
  deleteMessage = "Are you sure you want to delete the selected row?";

  constructor(
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController
  ) {}

  async onDelete<T>(
    data: T,
    deleteFn: (data: T) => Promise<void>,
    deleteMessage?: string,
    showAlert: boolean = true,
    headerMessage: string = "",
    successMessage: string = "Deleted Successfully",
    btnText: string = "Yes, Delete"
  ) {
    let message = this.deleteMessage;
    if (deleteMessage) message = deleteMessage;
    return await this._onDelete(
      data,
      message,
      deleteFn,
      showAlert,
      headerMessage,
      successMessage,
      btnText
    );
  }

  async _onDelete<T>(
    data: T,
    message: string,
    deleteFn: (data: T) => Promise<void>,
    showAlert: boolean,
    headerMessage: string = "",
    successMessage: string = "Deleted Successfully",
    btnText: string = "Yes, Delete"
  ) {
    let alertRole: string | undefined;
    if (showAlert) {
      let alert = await this.alertCtrl.create({
        header: headerMessage ? headerMessage : "What would you like to do?",
        message: message,
        buttons: [
          { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
          {
            text: btnText,
            role: "confirm",
            cssClass: "confirm-btn-delete",
          },
        ],
        mode: "md",
        cssClass: "delete-alert",
      });
      await alert.present();
      let { role } = await alert.onDidDismiss();
      alertRole = role;
    }

    if (alertRole == "confirm" || showAlert == false) {
      let loading = await this.loadingCtrl.create({ message: "loading..." });
      await loading.present();
      try {
        await deleteFn(data);
        await loading.dismiss();
        /* Workaround on multiple calls successMessage will not shown */
        if (successMessage) {
          await this.toastDeleteSucccess(successMessage);
          return true;
        }
      } catch (e: any) {
        await loading.dismiss();
        if (e.status == 400) {
          this.alertDeleteError(
            e.error && e.error.non_field_errors
              ? e.error.non_field_errors
              : `Unable to delete`
          );
        } else if (e.status == 403) {
          this.alertDeleteError(e.error.detail);
        } else if (e.status == 404) {
          this.alertDeleteError(
            "Looks like the page you're trying to visit does not exist. Please check the URL and try again.",
            "404 Error"
          );
        } else if (e.status == 500) {
          this.alertDeleteError(
            "Something went wrong, please reach out TromeTech support team for details"
          );
        } else if (e.status == 0) {
          this.alertDeleteError(
            "We appreciate your patience and understanding. We are experiencing heavy traffic to our services. Please try again later."
          );
        } else if (e.status == 429) {
          this.alertDeleteError(
            "We are unable to process your request at this time. Please try again later"
          );
        }
      }
    }
  }

  async alertDeleteError(message: string, headText?: string) {
    let alertView = await this.alertCtrl.create({
      header: headText ? headText : "Delete Error",
      message: message,
      buttons: [{ text: "Ok", role: "cancel", cssClass: "confirm-btn" }],
      mode: "md",
    });
    alertView.present();
  }

  async toastDeleteSucccess(message: string) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 3000,
      color: "primary",
    });
    toast.present();
  }
}
